import '../styles/GlobalDesign.css';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';

const API_URL = process.env.NODE_ENV === 'production' 
  ? '/api'
  : process.env.REACT_APP_API_URL || '/api';

const AdminPromoCodes = () => {
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [selectedPromo, setSelectedPromo] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: '',
    max_uses: '',
    valid_from: '',
    valid_until: '',
    applicable_plans: null, // null = tous les plans, [] ou [plans] = plans spécifiques
    is_active: 1
  });

  const PLANS = [
    { id: 'premium',     name: 'Premium - 1 Mois' },
    { id: 'pro',         name: 'Pro - 3 Mois' },
    { id: 'semi_annual', name: 'Semi-Annuel - 6 Mois' },
    { id: 'ultimate',    name: 'Ultimate - 1 An' }
  ];

  useEffect(() => {
    fetchPromoCodes();
  }, []);

  const fetchPromoCodes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/promo-codes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPromoCodes(response.data.promoCodes || []);
      setLoading(false);
    } catch (error) {
      console.error('Erreur chargement codes promo:', error);
      setError('Erreur lors du chargement des codes promo');
      setLoading(false);
    }
  };

  const generateRandomCode = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/promo-codes/generate`,
        { prefix: 'PROMO', length: 8 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFormData({ ...formData, code: response.data.code });
    } catch (error) {
      console.error('Erreur génération code:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      discount_type: 'percentage',
      discount_value: '',
      max_uses: '',
      valid_from: '',
      valid_until: '',
      applicable_plans: null,
      is_active: 1
    });
    setShowCreateForm(false);
    setShowEditForm(false);
    setSelectedPromo(null);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      const data = {
        code: formData.code.toUpperCase(),
        discount_type: formData.discount_type,
        discount_value: parseFloat(formData.discount_value),
        max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
        valid_from: formData.valid_from || null,
        valid_until: formData.valid_until || null,
        applicable_plans: formData.applicable_plans && formData.applicable_plans.length > 0 ? formData.applicable_plans : null,
        is_active: formData.is_active
      };

      if (showEditForm && selectedPromo) {
        await axios.put(
          `${API_URL}/promo-codes/${selectedPromo.id}`,
          data,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSuccess('Code promo modifié avec succès !');
      } else {
        await axios.post(
          `${API_URL}/promo-codes`,
          data,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSuccess('Code promo créé avec succès !');
      }

      fetchPromoCodes();
      resetForm();
    } catch (error) {
      setError(error.response?.data?.error || 'Erreur lors de l\'opération');
    }
  };

  const handleEdit = (promo) => {
    setSelectedPromo(promo);
    setFormData({
      code: promo.code,
      discount_type: promo.discount_type,
      discount_value: promo.discount_value.toString(),
      max_uses: promo.max_uses?.toString() || '',
      valid_from: promo.valid_from ? promo.valid_from.substring(0, 16) : '',
      valid_until: promo.valid_until ? promo.valid_until.substring(0, 16) : '',
      applicable_plans: promo.applicable_plans ? JSON.parse(promo.applicable_plans) : [],
      is_active: promo.is_active
    });
    setShowEditForm(true);
    setShowCreateForm(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce code promo ?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/promo-codes/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Code promo supprimé avec succès !');
      fetchPromoCodes();
    } catch (error) {
      setError('Erreur lors de la suppression');
    }
  };

  const toggleStatus = async (id, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/promo-codes/${id}`,
        { is_active: currentStatus ? 0 : 1 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(`Code promo ${currentStatus ? 'désactivé' : 'activé'} !`);
      fetchPromoCodes();
    } catch (error) {
      setError('Erreur lors du changement de statut');
    }
  };

  const handlePlanToggle = (planId) => {
    // Si applicable_plans est null, on initialise avec un tableau vide
    const plans = formData.applicable_plans === null ? [] : [...formData.applicable_plans];
    const index = plans.indexOf(planId);
    
    if (index > -1) {
      plans.splice(index, 1);
    } else {
      plans.push(planId);
    }
    
    setFormData({ ...formData, applicable_plans: plans });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Jamais';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-content">
          <div className="loading">Chargement...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-content">
        <div className="promo-header">
          <h1>🎫 Gestion des Codes Promo</h1>
          <button
            className="btn-primary"
            onClick={() => {
              resetForm();
              setShowCreateForm(!showCreateForm);
            }}
          >
            {showCreateForm ? '✕ Annuler' : '+ Nouveau Code Promo'}
          </button>
        </div>

        {error && (
          <div className="alert alert-error">
            <span className="alert-icon">⚠️</span> {error}
            <button onClick={() => setError('')} className="alert-close">✕</button>
          </div>
        )}

        {success && (
          <div className="alert alert-success">
            <span className="alert-icon">✅</span> {success}
            <button onClick={() => setSuccess('')} className="alert-close">✕</button>
          </div>
        )}

        {(showCreateForm || showEditForm) && (
          <div className="promo-form-card">
            <h2>{showEditForm ? '✏️ Modifier le Code Promo' : '➕ Créer un Code Promo'}</h2>
            <form onSubmit={handleSubmit} className="promo-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Code Promo *</label>
                  <div className="input-with-button">
                    <input
                      type="text"
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                      placeholder="Ex: SUMMER2024"
                      required
                      disabled={showEditForm}
                    />
                    {!showEditForm && (
                      <button type="button" onClick={generateRandomCode} className="btn-secondary">
                        🎲 Générer
                      </button>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label>Type de Réduction *</label>
                  <select
                    value={formData.discount_type}
                    onChange={(e) => setFormData({ ...formData, discount_type: e.target.value })}
                    required
                  >
                    <option value="percentage">Pourcentage (%)</option>
                    <option value="fixed">Montant Fixe (FCFA)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Valeur *</label>
                  <input
                    type="number"
                    value={formData.discount_value}
                    onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                    placeholder={formData.discount_type === 'percentage' ? 'Ex: 20' : 'Ex: 1000'}
                    min="0"
                    max={formData.discount_type === 'percentage' ? '100' : undefined}
                    step={formData.discount_type === 'percentage' ? '1' : '100'}
                    required
                  />
                  <small>{formData.discount_type === 'percentage' ? '% de réduction' : 'FCFA de réduction'}</small>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Utilisations Max</label>
                  <input
                    type="number"
                    value={formData.max_uses}
                    onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
                    placeholder="Illimité si vide"
                    min="1"
                  />
                  <small>Laissez vide pour illimité</small>
                </div>

                <div className="form-group">
                  <label>Valide Depuis</label>
                  <input
                    type="datetime-local"
                    value={formData.valid_from}
                    onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Expire Le</label>
                  <input
                    type="datetime-local"
                    value={formData.valid_until}
                    onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Plans Applicables</label>
                <div className="checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.applicable_plans === null}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData({ ...formData, applicable_plans: null });
                        } else {
                          setFormData({ ...formData, applicable_plans: [] });
                        }
                      }}
                    />
                    <span>Tous les plans</span>
                  </label>
                  {PLANS.map(plan => (
                    <label key={plan.id} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.applicable_plans !== null && formData.applicable_plans.includes(plan.id)}
                        onChange={() => handlePlanToggle(plan.id)}
                        disabled={formData.applicable_plans === null}
                      />
                      <span>{plan.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label className="checkbox-label status-toggle">
                  <input
                    type="checkbox"
                    checked={formData.is_active === 1}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked ? 1 : 0 })}
                  />
                  <span>Code Actif</span>
                </label>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-primary">
                  {showEditForm ? '💾 Enregistrer' : '✨ Créer le Code'}
                </button>
                <button type="button" onClick={resetForm} className="btn-secondary">
                  ✕ Annuler
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="promo-stats">
          <div className="stat-card">
            <div className="stat-value">{promoCodes.length}</div>
            <div className="stat-label">Codes Total</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{promoCodes.filter(p => p.is_active).length}</div>
            <div className="stat-label">Codes Actifs</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{promoCodes.reduce((sum, p) => sum + p.used_count, 0)}</div>
            <div className="stat-label">Utilisations Total</div>
          </div>
        </div>

        <div className="promo-table-container">
          <table className="promo-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Type</th>
                <th>Réduction</th>
                <th>Utilisations</th>
                <th>Validité</th>
                <th>Plans</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {promoCodes.length === 0 ? (
                <tr>
                  <td colSpan="8" className="empty-state">
                    <div className="empty-icon">🎫</div>
                    <p>Aucun code promo créé</p>
                    <button onClick={() => setShowCreateForm(true)} className="btn-primary">
                      Créer le premier code
                    </button>
                  </td>
                </tr>
              ) : (
                promoCodes.map((promo) => (
                  <tr key={promo.id} className={!promo.is_active ? 'inactive-row' : ''}>
                    <td>
                      <strong className="promo-code">{promo.code}</strong>
                    </td>
                    <td>
                      <span className={`badge badge-${promo.discount_type}`}>
                        {promo.discount_type === 'percentage' ? 'Pourcentage' : 'Fixe'}
                      </span>
                    </td>
                    <td>
                      <strong className="discount-value">
                        {promo.discount_type === 'percentage' 
                          ? `${promo.discount_value}%` 
                          : `${promo.discount_value} FCFA`}
                      </strong>
                    </td>
                    <td>
                      <div className="usage-info">
                        <span className="usage-count">{promo.used_count}</span>
                        {promo.max_uses && (
                          <>
                            <span className="usage-separator">/</span>
                            <span className="usage-max">{promo.max_uses}</span>
                          </>
                        )}
                        {!promo.max_uses && <span className="usage-unlimited">∞</span>}
                      </div>
                    </td>
                    <td>
                      <div className="validity-info">
                        {promo.valid_until ? (
                          <span className={new Date(promo.valid_until) < new Date() ? 'expired' : 'valid'}>
                            {formatDate(promo.valid_until)}
                          </span>
                        ) : (
                          <span className="no-expiry">Pas d'expiration</span>
                        )}
                      </div>
                    </td>
                    <td>
                      {promo.applicable_plans ? (
                        <div className="plans-list">
                          {JSON.parse(promo.applicable_plans).map(p => (
                            <span key={p} className="plan-badge">{p}</span>
                          ))}
                        </div>
                      ) : (
                        <span className="all-plans">Tous</span>
                      )}
                    </td>
                    <td>
                      <button
                        onClick={() => toggleStatus(promo.id, promo.is_active)}
                        className={`status-btn ${promo.is_active ? 'active' : 'inactive'}`}
                      >
                        {promo.is_active ? '✓ Actif' : '✕ Inactif'}
                      </button>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          onClick={() => handleEdit(promo)}
                          className="btn-edit"
                          title="Modifier"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDelete(promo.id)}
                          className="btn-delete"
                          title="Supprimer"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminPromoCodes;
